import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  Badge,
  Collapse,
  IconButton
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import logo from '../../images/logo_PTG_retina.png'
import DashboardIcon from '@mui/icons-material/Dashboard'
import DescriptionIcon from '@mui/icons-material/Description'
import CalculateIcon from '@mui/icons-material/Calculate'
import BusinessIcon from '@mui/icons-material/Business'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import AnalyticsIcon from '@mui/icons-material/Analytics'
import StorageIcon from '@mui/icons-material/Storage'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import TimelineIcon from '@mui/icons-material/Timeline'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import EngineeringIcon from '@mui/icons-material/Engineering'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import SchoolIcon from '@mui/icons-material/School'
import HomeIcon from '@mui/icons-material/Home'
import MenuIcon from '@mui/icons-material/Menu'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useComparison } from '../../context/ComparisonContext'
import { useThemeMode } from '../../context/ThemeContext'
import { tokens } from '../../theme'

const DRAWER_WIDTH = 260

const menuItems = [
  { text: 'Home', icon: <HomeIcon />, path: '/' },
  { text: 'Ask AI', icon: <SmartToyIcon />, path: '/ai-assistant', tourId: 'tour-ai' },
  { text: 'Education', icon: <SchoolIcon />, path: '/education' },
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', tourId: 'tour-dashboard' },
  { text: 'Case Studies', icon: <DescriptionIcon />, path: '/case-studies' },
  { text: 'Compare', icon: <CompareArrowsIcon />, path: '/compare' },
  {
    text: 'Analysis',
    icon: <AnalyticsIcon />,
    path: '/analysis',
    tourId: 'tour-analysis',
    children: [
      { text: 'Longevity & ASHRAE', path: '/analysis/longevity', icon: <TimelineIcon /> },
      { text: 'Equipment Analysis', path: '/analysis/equipment', icon: <PrecisionManufacturingIcon /> },
      { text: 'Engineering Metrics', path: '/analysis/engineering', icon: <EngineeringIcon /> },
      { text: 'Trends & Correlation', path: '/analysis/trends', icon: <TrendingUpIcon /> }
    ]
  },
  { text: 'Data Explorer', icon: <StorageIcon />, path: '/data-explorer', tourId: 'tour-explorer' },
  { text: 'ROI Calculator', icon: <CalculateIcon />, path: '/roi-calculator', tourId: 'tour-roi' },
  { text: 'Fleet Impact', icon: <BusinessIcon />, path: '/fleet-impact', tourId: 'tour-fleet' },
  { text: 'EAS Calculator', icon: <UploadFileIcon />, path: '/eas-calculator', tourId: 'tour-eas' }
]

function Sidebar({ mobileOpen, onClose }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { itemCount } = useComparison()
  const { mode, toggleTheme } = useThemeMode()
  const theme = useTheme()
  const palette = theme.palette
  const [expandedItems, setExpandedItems] = useState({})

  // Auto-expand Analysis menu when on any analysis sub-route
  useEffect(() => {
    if (location.pathname.startsWith('/analysis')) {
      setExpandedItems(prev => ({ ...prev, 'Analysis': true }))
    }
  }, [location.pathname])

  const handleNavigation = (path) => {
    navigate(path)
    onClose()
  }

  const toggleExpand = (text) => {
    setExpandedItems(prev => ({ ...prev, [text]: !prev[text] }))
  }

  const getIcon = (item) => {
    if (item.text === 'Compare' && itemCount > 0) {
      return (
        <Badge
          badgeContent={itemCount}
          color="primary"
          sx={{
            '& .MuiBadge-badge': {
              background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.primary.dark} 100%)`,
              color: palette.primary.contrastText,
              fontWeight: 700,
              fontSize: '0.65rem'
            }
          }}
        >
          {item.icon}
        </Badge>
      )
    }
    return item.icon
  }

  const isItemSelected = (item) => {
    if (item.children) {
      return item.children.some(child => location.pathname === child.path)
    }
    return location.pathname === item.path
  }

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: palette.background.paper,
        borderRight: `1px solid ${palette.divider}`
      }}
    >
      {/* Logo Header */}
      <Box
        sx={{
          height: 72,
          minHeight: 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: alpha(palette.background.default, 0.5),
          borderBottom: `1px solid ${palette.divider}`,
          px: 2
        }}
      >
        <a
          href="https://www.powertronglobal.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <img
            src={logo}
            alt="Powertron Global Energy Solutions"
            style={{
              height: 48,
              width: 'auto',
              cursor: 'pointer',
              filter: 'brightness(1.1)'
            }}
          />
        </a>
      </Box>

      {/* Navigation List */}
      <List sx={{ flexGrow: 1, pt: 2, px: 1 }}>
        {menuItems.map((item, index) => (
          <Box key={item.text || `divider-${index}`}>
            {item.divider ? (
              <Divider
                sx={{
                  my: 1.5,
                  mx: 1,
                  borderColor: palette.divider
                }}
              />
            ) : item.children ? (
              // Menu item with children (expandable)
              <>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    data-tour={item.tourId || undefined}
                    onClick={() => toggleExpand(item.text)}
                    selected={isItemSelected(item) && !expandedItems[item.text]}
                    sx={{
                      borderRadius: tokens.radius.sm,
                      py: 1.25,
                      transition: `all ${tokens.transitions.fast}`,
                      '&:hover': {
                        bgcolor: alpha(palette.primary.main, 0.1),
                        '& .MuiListItemIcon-root': {
                          color: palette.primary.main
                        }
                      },
                      '&.Mui-selected': {
                        bgcolor: alpha(palette.primary.main, 0.15),
                        borderLeft: `3px solid ${palette.primary.main}`,
                        '&:hover': {
                          bgcolor: alpha(palette.primary.main, 0.2)
                        },
                        '& .MuiListItemIcon-root': {
                          color: palette.primary.main
                        }
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: palette.text.secondary,
                        transition: `color ${tokens.transitions.fast}`
                      }}
                    >
                      {getIcon(item)}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: 500,
                        fontSize: '0.9rem'
                      }}
                    />
                    {expandedItems[item.text] ? (
                      <ExpandLess sx={{ color: palette.text.secondary }} />
                    ) : (
                      <ExpandMore sx={{ color: palette.text.secondary }} />
                    )}
                  </ListItemButton>
                </ListItem>
                <Collapse in={expandedItems[item.text]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map(child => (
                      <ListItem key={child.path} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => handleNavigation(child.path)}
                          selected={location.pathname === child.path}
                          sx={{
                            pl: 4,
                            py: 1,
                            borderRadius: tokens.radius.sm,
                            transition: `all ${tokens.transitions.fast}`,
                            '&:hover': {
                              bgcolor: alpha(palette.primary.main, 0.1),
                              '& .MuiListItemIcon-root': {
                                color: palette.primary.main
                              }
                            },
                            '&.Mui-selected': {
                              bgcolor: alpha(palette.primary.main, 0.2),
                              '&:hover': {
                                bgcolor: alpha(palette.primary.main, 0.25)
                              },
                              '& .MuiListItemIcon-root': {
                                color: palette.primary.main
                              },
                              '& .MuiListItemText-primary': {
                                color: palette.primary.main,
                                fontWeight: 600
                              }
                            }
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 32,
                              color: palette.text.secondary,
                              transition: `color ${tokens.transitions.fast}`,
                              '& .MuiSvgIcon-root': {
                                fontSize: 18
                              }
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={child.text}
                            primaryTypographyProps={{
                              fontSize: '0.8rem',
                              fontWeight: 500
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              // Regular menu item
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  data-tour={item.tourId || undefined}
                  onClick={() => handleNavigation(item.path)}
                  selected={location.pathname === item.path}
                  sx={{
                    borderRadius: tokens.radius.sm,
                    py: 1.25,
                    transition: `all ${tokens.transitions.fast}`,
                    '&:hover': {
                      bgcolor: alpha(palette.primary.main, 0.1),
                      '& .MuiListItemIcon-root': {
                        color: palette.primary.main
                      }
                    },
                    '&.Mui-selected': {
                      bgcolor: alpha(palette.primary.main, 0.15),
                      borderLeft: `3px solid ${palette.primary.main}`,
                      '&:hover': {
                        bgcolor: alpha(palette.primary.main, 0.2)
                      },
                      '& .MuiListItemIcon-root': {
                        color: palette.primary.main
                      },
                      '& .MuiListItemText-primary': {
                        color: palette.text.primary,
                        fontWeight: 600
                      }
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: palette.text.secondary,
                      transition: `color ${tokens.transitions.fast}`
                    }}
                  >
                    {getIcon(item)}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: '0.9rem'
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </Box>
        ))}
      </List>

      {/* Footer with Theme Toggle */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${palette.divider}`,
          background: alpha(palette.background.default, 0.3)
        }}
      >
        {/* Theme Toggle */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.5,
            gap: 1
          }}
        >
          <IconButton
            onClick={toggleTheme}
            size="small"
            sx={{
              color: palette.text.secondary,
              bgcolor: alpha(palette.primary.main, 0.1),
              '&:hover': {
                bgcolor: alpha(palette.primary.main, 0.2),
                color: palette.primary.main
              }
            }}
            aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
          <IconButton
            onClick={() => window.dispatchEvent(new CustomEvent('restart-tour'))}
            size="small"
            sx={{
              color: palette.text.secondary,
              bgcolor: alpha(palette.primary.main, 0.1),
              '&:hover': {
                bgcolor: alpha(palette.primary.main, 0.2),
                color: palette.primary.main
              }
            }}
            aria-label="Restart quick tour"
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Version */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            color: palette.text.disabled,
            fontSize: '0.7rem',
            fontFamily: '"JetBrains Mono", monospace'
          }}
        >
          v1.0.0 | PermaFrost NMR
        </Typography>
      </Box>
    </Box>
  )

  return (
    <Box
      component="nav"
      id="navigation"
      aria-label="Main navigation"
      sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            bgcolor: palette.background.paper,
            borderRight: `1px solid ${palette.divider}`
          }
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            position: 'fixed',
            height: '100vh',
            overflowY: 'auto',
            bgcolor: palette.background.paper,
            borderRight: `1px solid ${palette.divider}`,
            // Custom scrollbar
            '&::-webkit-scrollbar': {
              width: 4
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              background: alpha('#ffffff', 0.2),
              borderRadius: 2,
              '&:hover': {
                background: alpha('#ffffff', 0.3)
              }
            }
          }
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  )
}

export default Sidebar
